import { useState, useMemo } from 'react';
import { Link, useSearch, useNavigate } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import { useLoads } from '@/data/loads';
import { FirstRunCard } from '@/components/first-run-card';
import { useDrivers } from '@/data/members';
import { useCustomers } from '@/data/customers';
import { StatusBadge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { STATUS_FILTERS, ACTIVE_STATUSES, motionLabel } from '@/lib/status';
import { formatDateTime, formatMoney, shortId } from '@/lib/format';
import { cn } from '@/lib/cn';
import { TableScroll } from '@/components/ui/table-scroll';
import type { LoadListItem, LoadStatus } from '@breaker/shared-types';

const PAGE_SIZE = 50;

type FilterKey = LoadStatus | 'open' | null;

export function LoadsPage() {
  const { fleet, isLoading: fleetLoading } = useActiveFleet();
  const search = useSearch({ from: '/authed/' });
  const navigate = useNavigate();

  const driverFilter = (search as { driverUserId?: string }).driverUserId ?? undefined;
  const customerFilter = (search as { customerId?: string }).customerId ?? undefined;

  const driversQuery = useDrivers(fleet?.id ?? null);
  const customersQuery = useCustomers(fleet?.id ?? null, { limit: 100 });

  const driverName = driverFilter
    ? driversQuery.data?.find((d) => d.userId === driverFilter)?.user.displayName
    : undefined;
  const customerName = customerFilter
    ? customersQuery.data?.find((c) => c.id === customerFilter)?.name
    : undefined;

  function clearScopedFilters() {
    navigate({ to: '/', search: {} });
  }

  const [filter, setFilter] = useState<FilterKey>(null);
  const [offset, setOffset] = useState(0);

  // Build the API filter — "open" is synthetic and post-filtered client-side.
  const apiFilters = useMemo(() => {
    const f: {
      status?: LoadStatus;
      driverUserId?: string;
      customerId?: string;
      limit: number;
      offset: number;
    } = {
      limit: PAGE_SIZE,
      offset,
    };
    if (filter && filter !== 'open') f.status = filter;
    if (driverFilter) f.driverUserId = driverFilter;
    if (customerFilter) f.customerId = customerFilter;
    return f;
  }, [filter, offset, driverFilter, customerFilter]);

  const loadsQuery = useLoads(fleet?.id ?? null, apiFilters);

  const loads = useMemo(() => {
    if (!loadsQuery.data) return [];
    if (filter === 'open') {
      return loadsQuery.data.filter((d) => ACTIVE_STATUSES.includes(d.status));
    }
    return loadsQuery.data;
  }, [loadsQuery.data, filter]);

  function handleFilterChange(next: FilterKey) {
    setFilter(next);
    setOffset(0);
  }

  return (
    <div className="px-8 py-10 max-w-[1400px]">
      <FirstRunCard />
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
            Loads
          </h1>
        </div>
        <Link to="/loads/new">
          <Button variant="primary">New load</Button>
        </Link>
      </header>

      {/* One row, always. Seven chips wrap to two rows at 390px otherwise,
          which shifts the table down and reads as a broken toolbar. */}
      <div className="flex scroll-row gap-1.5 mb-5 items-center">
        {STATUS_FILTERS.map((opt) => {
          const active = filter === opt.key;
          return (
            <button
              key={opt.label}
              type="button"
              onClick={() => handleFilterChange(opt.key)}
              className={cn(
                'px-3 h-7 shrink-0 whitespace-nowrap rounded-md text-xs font-medium transition-colors',
                active
                  ? 'bg-breaker-ink text-white'
                  : 'bg-white text-breaker-ink-2 border border-breaker-line-2 hover:bg-breaker-ground-2',
              )}
            >
              {opt.label}
            </button>
          );
        })}

        {driverFilter || customerFilter ? (
          <>
            <span className="h-4 w-px shrink-0 bg-breaker-line mx-1" />
            <span className="inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 px-3 h-7 rounded-md text-xs font-medium bg-breaker-identity/10 text-breaker-identity-hover border border-breaker-identity/30">
              {driverFilter ? (
                <>Driver: {driverName ?? <span className="ref">{shortId(driverFilter)}</span>}</>
              ) : (
                <>Customer: {customerName ?? <span className="ref">{shortId(customerFilter ?? null)}</span>}</>
              )}
              <button
                type="button"
                onClick={clearScopedFilters}
                className="ml-1 -mr-1 hover:text-breaker-identity-hover"
                aria-label="Clear scoped filter"
              >
                ×
              </button>
            </span>
          </>
        ) : null}
      </div>

      {fleetLoading || loadsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : loadsQuery.isError ? (
        <EmptyState
          title="Couldn't load loads"
          description={
            (loadsQuery.error as Error)?.message ??
            'The server returned an error. Try again in a moment.'
          }
          action={
            <Button onClick={() => loadsQuery.refetch()} variant="secondary" size="sm">
              Retry
            </Button>
          }
        />
      ) : loads.length === 0 ? (
        <EmptyState
          title={filter ? 'No loads match this filter' : 'No loads yet'}
          description={
            filter
              ? 'Try a different filter or clear it to see everything.'
              : 'Create a load to get started — pickup, dropoff, assign a driver.'
          }
        />
      ) : (
        <LoadTable loads={loads} />
      )}

      {loads.length > 0 ? (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-breaker-mute">
            Showing {offset + 1}–{offset + loads.length}
            {loads.length === PAGE_SIZE ? ' (more available)' : ''}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={loads.length < PAGE_SIZE}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface LoadTableProps {
  loads: LoadListItem[];
}

function LoadTable({ loads }: LoadTableProps) {
  return (
    <div className="surface rounded-lg overflow-hidden">
      <TableScroll>
        <table className="w-full text-sm">
        <thead className="bg-breaker-ground-2 border-b border-breaker-line">
          <tr className="text-left">
            <Th className="w-[180px]">Reference</Th>
            <Th className="w-[140px]">Status</Th>
            <Th>Customer</Th>
            <Th>Driver</Th>
            <Th className="w-[170px]">Scheduled</Th>
            <Th className="w-[110px] text-right">Total</Th>
          </tr>
        </thead>
        <tbody>
          {loads.map((d) => (
            <tr
              key={d.id}
              className="border-b last:border-b-0 border-breaker-line hover:bg-breaker-ground-2 transition-colors"
            >
              <Td>
                <Link
                  to="/loads/$loadId"
                  params={{ loadId: d.id }}
                  className="font-mono font-medium text-breaker-ink hover:text-breaker-identity transition-colors"
                >
                  {d.reference}
                </Link>
              </Td>
              <Td>
                <StatusBadge status={d.status} />
                {d.status === 'assigned' && d.motion ? (
                  <p className="text-2xs text-breaker-mute mt-1">
                    {motionLabel(d.motion)}
                  </p>
                ) : null}
              </Td>
              <Td>
                {d.customerName ? (
                  d.customerName
                ) : d.customerId ? (
                  <span className="ref">{shortId(d.customerId)}</span>
                ) : (
                  <span className="text-breaker-mute">—</span>
                )}
              </Td>
              <Td>
                {d.assignedDriverName ? (
                  d.assignedDriverName
                ) : d.assignedDriverUserId ? (
                  <span className="ref">{shortId(d.assignedDriverUserId)}</span>
                ) : (
                  <span className="text-breaker-mute italic">unassigned</span>
                )}
              </Td>
              <Td className="text-breaker-ink-2">
                {formatDateTime(d.scheduledStartAt)}
              </Td>
              <Td className="text-right tabular-nums">
                {formatMoney(d.totalPence)}
              </Td>
            </tr>
          ))}
        </tbody>
        </table>
      </TableScroll>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'px-4 py-2.5 text-2xs font-medium uppercase tracking-wider text-breaker-ink-2',
        className,
      )}
    >
      {children}
    </th>
  );
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 align-middle', className)}>{children}</td>;
}
